// ResetPasswordModal.tsx
import React from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button as MuiButton, // Alias to avoid conflict with your bootstrap Button
  Box,
} from "@mui/material";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ToastComponent"; // Assuming you want to use toasts here too
import { CapitalizeFirstLetter } from "./CapitalizeFirstLetter";

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  dataSourceId: number; // Or any identifier needed for the API
  currentFormData: DataSourceFormValues;
  vyu_api_url: string | undefined;
}

type ResetPasswordFormValues = {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};
export type DataSourceFormValues = {
  name: string;
  url: string;
  userName: string;
  pwd: string;
  driverName: string;
  driverType: string;
  dbType: string;
  // createUser might not be needed for password reset
};

export default function ResetPasswordModal({
  open,
  onClose,
  dataSourceId,
  currentFormData,
  vyu_api_url,
}: ResetPasswordModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ResetPasswordFormValues>();
  const [successMessage, setSuccessMessage] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  // console.log("Printing props in Reset password Modal", currentFormData);

  const handleSuccess = (message: string) => {
    showSuccessToast(message);
  };
  const handleError = (message: string) => {
    showErrorToast(message);
  };

  const handleResetPassword = async (data: ResetPasswordFormValues) => {
    if (data.newPassword !== data.confirmNewPassword) {
      showErrorToast("New passwords do not match.");
      return;
    }

    console.log("Resetting password for ID:", dataSourceId, "with data:", data);
    const token = localStorage.getItem("token");
    currentFormData.name = CapitalizeFirstLetter(currentFormData.name);
    const formDataObject = {
      ...currentFormData,
      pwd: data.newPassword,
    };
    try {
      const response = await fetch(
        `${vyu_api_url}/datasource/updateById?id=${dataSourceId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formDataObject),
        }
      );
      if (response.ok) {
        setSuccessMessage("Reset password successfully!");
        handleSuccess("Reset password successfully!");
        reset();
        onClose();
      } else {
        const data = await response.json();
        setErrorMessage(data.message || "Failed to update DataSource!");
        handleError(data.message || "Failed to create DataSource!");
      }
    } catch (e) {
      console.error("Error creating DataSource:", e);
      setErrorMessage("An error occurred while creating DataSource");
    }
  };
  React.useEffect(() => {
    console.log("Printing props in Reset password Modal", currentFormData);
  }, [currentFormData]);
  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Reset Data Source Password</DialogTitle>
      <form onSubmit={handleSubmit(handleResetPassword)}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* <TextField
              autoFocus
              label="Old Password"
              type="password"
              fullWidth
              variant="outlined"
              {...register("oldPassword", {
                required: "Old password is required",
              })}
              error={!!errors.oldPassword}
              helperText={errors.oldPassword?.message}
            /> */}
            <TextField
              label="Enter New Password"
              type="password"
              fullWidth
              variant="outlined"
              {...register("newPassword", {
                required: "New password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
            />
            <TextField
              label="Enter Confirm New Password"
              type="password"
              fullWidth
              variant="outlined"
              {...register("confirmNewPassword", {
                required: "Please confirm your new password",
                validate: (value, formValues) =>
                  value === formValues.newPassword || "Passwords do not match",
              })}
              error={!!errors.confirmNewPassword}
              helperText={errors.confirmNewPassword?.message}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <MuiButton
            onClick={handleCancel}
            color="inherit"
            style={{
              backgroundColor: "white",
              border: "1px solid #d0d5dd",
              color: "#0D57DE",
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            type="submit"
            variant="contained"
            color="primary"
            style={{
              backgroundColor: "#0D57DE",
              border: "1px solid #0D57DE",
              color: "white",
            }}
          >
            Reset Password
          </MuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
